import classNames from "classnames";
import { forwardRef, Fragment, useRef, useState } from "react";
import { Listbox, ListboxProps, Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import { twMerge } from "tailwind-merge";
import SelectOption, { SelectOptionProps } from "./SelectOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import List from "@/components/UI/List/List";
import Portal from "@/components/UI/Portal/Portal";

// See: https://tailwindui.com/components/application-ui/forms/select-menus
// See: https://headlessui.com/react/listbox

const SelectShadow = {
  none: "none",
  small: "small",
  medium: "medium",
} as const;

const SelectSize = {
  small2x: "small2x",
  small: "small",
  medium: "medium",
  large: "large",
} as const;

const SelectState = {
  default:
    "bg-white border-vpGray-300 focus:border-vpBlue-500 focus:ring-vpBlue-500 focus:ring-1 hover:border-vpBlue-500 outline-0",
  disabled: "border-vpGray-300 bg-vpGray-200 placeholder:text-vpGray-500 text-vpGray-500 cursor-default",
  error:
    "bg-vpRed-100 border-vpRed-500 focus:ring-vpRed-500 focus:border-vpRed-500 focus:ring-1 hover:border-vpRed-500 outline-0",
} as const;

const SelectShadowConfig = {
  [SelectShadow.none]: "",
  [SelectShadow.small]: "shadow-sm",
  [SelectShadow.medium]: "shadow-md",
};

const SelectSizeConfig = {
  [SelectSize.small2x]: {
    button: "px-2 py-2 pr-9 text-sm",
  },
  [SelectSize.small]: {
    button: "px-3 py-2 pr-9 text-md",
  },
  [SelectSize.medium]: {
    button: "px-3 py-2 pr-9 text-lg",
  },
  [SelectSize.large]: {
    button: "px-3 py-3 pr-9 text-xl",
  },
};

export type SelectRootNode = HTMLElement;

export type SelectProps = ListboxProps<"ul", unknown, unknown> & {
  error?: boolean;
  options?: SelectOptionProps[];
  placeholder?: string;
  shadow?: keyof typeof SelectShadow;
  size?: keyof typeof SelectSize;
  slots?: {
    button?: {
      css?: {
        content?: string;
      };
      props?: Parameters<typeof Listbox.Button>[0] & {
        children?: React.ReactNode;
      };
    };
    popper?: {
      css?: {
        root?: string;
      };
    };
  };
  startNode?: React.ReactNode;
};

const Select = forwardRef<SelectRootNode, SelectProps>(
  (
    {
      children,
      disabled,
      error,
      id,
      placeholder,
      shadow = SelectShadow.medium,
      size = SelectSize.medium,
      slots,
      startNode,
      options = [],
      ...props
    },
    ref
  ) => {
    const popperRef = useRef(null);

    const [popperElement, setPopperElement] = useState(null);
    const [targetElement, setTargetElement] = useState<HTMLButtonElement | null>(null);

    const popper = usePopper(targetElement, popperElement, {
      placement: "bottom",
      strategy: "fixed",
    });

    return (
      <Listbox disabled={disabled} {...props} ref={ref}>
        {(renderProps) => {
          return (
            <div className="relative">
              <Listbox.Button
                className={twMerge(
                  classNames(
                    "relative w-full flex-1 cursor-pointer rounded-md border transition focus:outline-none",
                    SelectState[error ? "error" : disabled ? "disabled" : "default"],
                    SelectSizeConfig[size].button
                  )
                )}
                id={id}
                ref={setTargetElement}
              >
                <div
                  className={twMerge(
                    classNames(
                      "flex items-center gap-2 truncate text-left",
                      {
                        "text-vpGray-500": !renderProps.value && !slots?.button?.props?.children,
                      },
                      slots?.button?.css?.content
                    )
                  )}
                >
                  {startNode && startNode}
                  {slots?.button?.props?.children
                    ? slots.button.props.children
                    : renderProps.value
                    ? options.find((option) => option.value === renderProps.value)?.children
                    : placeholder}
                </div>
                <div className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                  <FontAwesomeIcon
                    aria-hidden="true"
                    className={classNames("text-gray-400", {
                      "rotate-180": false,
                    })}
                    icon={faChevronDown}
                  />
                </div>
              </Listbox.Button>
              <Portal>
                <div
                  className={twMerge(classNames("z-50", slots?.popper?.css?.root))}
                  ref={popperRef}
                  style={{
                    ...popper.styles.popper,
                    minWidth: targetElement?.scrollWidth,
                  }}
                  {...popper.attributes.popper}
                >
                  <Transition
                    beforeEnter={() => setPopperElement(popperRef.current)}
                    afterLeave={() => setPopperElement(null)}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                    show={renderProps.open}
                    as={Fragment}
                  >
                    <Listbox.Options
                      className={classNames(
                        "mt-1 max-h-52 overflow-auto rounded-md border border-vpGray-300 bg-white focus:outline-none ",
                        SelectShadowConfig[shadow]
                      )}
                    >
                      <List>
                        {options.map((option, index) => {
                          return <SelectOption key={index} {...option} />;
                        })}
                      </List>
                    </Listbox.Options>
                  </Transition>
                </div>
              </Portal>
            </div>
          );
        }}
      </Listbox>
    );
  }
);

Select.displayName = Select.name;

export default Select;
